import { Key } from "react";

import axios from "axios";

import MANAGER_ROUTES from "manager/config/apiRoutes";
import { PAGINATION } from "manager/config/constants";
import ROUTES, { VEGA_BASE_URL } from "shared/config/apiRoutes";
import { DEAL_UI_STATUS_CODES } from "shared/config/constants";
import { API_URL } from "shared/lib/api/setupApi";
import { transformRoute } from "shared/utils/routing";
import { notifyError } from "shared/utils/ui";

const buildDealsListParams = ({
  dateMin,
  dateMax,
  propertyId,
  unitId,
  unitPropertyOrApplicant,
  dealCategory,
  dealStatus,
  lastLumenResult,
  pageSize,
  page,
  ordering,
  isArchived,
  hasYardiStatus,
  isAnyApplicationFeePaid,
  employeeId,
  guarantorStatus,
  sentilinkRiskStatus,
  recommendationStatus,
  sentilinkBlocked,
}) => ({
  created_at__gte: dateMin,
  created_at__lte: dateMax,
  property_id__in: propertyId,
  unit_id: unitId,
  unit_or_property_or_applicant: unitPropertyOrApplicant,
  deal_category: dealCategory,
  deal_status: dealStatus,
  page_size: pageSize,
  last_lumen_score: lastLumenResult,
  page,
  ordering,
  is_archived: isArchived,
  is_any_application_fee_paid: isAnyApplicationFeePaid,
  has_yardi_status: hasYardiStatus,
  employee_id: employeeId,
  guarantor_status: guarantorStatus,
  sentilink_risk_status: sentilinkRiskStatus,
  recommendation_status: recommendationStatus,
  sentilink_blocked: sentilinkBlocked,
});

export const getDeals = (filters) =>
  axios
    .get(`${API_URL}${ROUTES.deals}`, {
      params: buildDealsListParams(filters),
      // @ts-ignore
      clearStorageAndReloadOnBadRequest: true,
    })
    .then(({ data }) => data);

// TI-2464: same params/response shape as getDeals, but hits the batched
// endpoint that skips per-applicant Cognito + person-service round trips.
// Only used by the manager /deals route (see useGetDealsFast) — other pages
// that consume the deal list (UnitDeals, Yardi resolver modals) intentionally
// stay on the legacy endpoint because their columns may read fields the fast
// path nulls out (application_link, location, fql_vrn, pms_unit_id).
export const getDealsFast = (filters) =>
  axios
    .get(`${API_URL}${ROUTES.dealsFast}`, {
      params: buildDealsListParams(filters),
      // @ts-ignore
      clearStorageAndReloadOnBadRequest: true,
    })
    .then(({ data }) => data);

export const getDealsCounts = ({
  dateMin,
  dateMax,
  propertyId,
  unitId,
  unitPropertyOrApplicant,
  dealStatus,
  lastLumenResult,
  isArchived,
  hasYardiStatus,
  isAnyApplicationFeePaid,
  employeeId,
  guarantorStatus,
  sentilinkRiskStatus,
  recommendationStatus,
  sentilinkBlocked,
}) =>
  axios
    .get(`${API_URL}${ROUTES.dealsCount}`, {
      params: {
        created_at__gte: dateMin,
        created_at__lte: dateMax,
        property_id__in: propertyId,
        unit_id: unitId,
        unit_or_property_or_applicant: unitPropertyOrApplicant,
        deal_status: dealStatus,
        last_lumen_score: lastLumenResult,
        is_archived: isArchived,
        is_any_application_fee_paid: isAnyApplicationFeePaid,
        has_yardi_status: hasYardiStatus,
        employee_id: employeeId,
        guarantor_status: guarantorStatus,
        sentilink_risk_status: sentilinkRiskStatus,
        recommendation_status: recommendationStatus,
        sentilink_blocked: sentilinkBlocked,
      },
      // @ts-ignore
      clearStorageAndReloadOnBadRequest: true,
    })
    .then(({ data }) => data);

export const getDeal = (
  id: string | number,
  options?: { mode?: "deal" }
) =>
  axios
    .get(transformRoute(`${API_URL}${ROUTES.deal}`, { id }), {
      params: options?.mode ? { mode: options.mode } : undefined,
    })
    .then(({ data }) => data);

export const updateDealArchiveStatus = ({ id, isArchived }) =>
  axios.patch(
    transformRoute(`${API_URL}${MANAGER_ROUTES.updateDealArchiveStatus}`, {
      id,
    }),
    { isArchived }
  );

export const assignDealToUnitCheck = ({ dealId, unitId, newLeaseStartDate }) =>
  axios
    .get(
      `${API_URL}${transformRoute(MANAGER_ROUTES.assignDealToUnitCheck, {
        id: dealId,
      })}`,
      {
        params: {
          target_unit_id: unitId,
          new_preferred_lease_start_date: newLeaseStartDate,
        },
      }
    )
    .then(({ data }) => data);

export type DealToMove = {
  id: string;
  applications: { firstName: string; lastName: string }[];
  propertyName: string;
  unitName: string;
  canApplicationBeMoved: boolean;
  dealStatus: DEAL_UI_STATUS_CODES;
};

export type DealsToMoveResponse = {
  totalCount: number;
  pageSize: number;
  offset: number;
  page: number;
  results: DealToMove[];
};

export const getDealsToMove = async (
  id: string | number,
  {
    limit,
    pageSize = PAGINATION.DEALS_TO_MOVE_PAGE_SIZE,
    page = 1,
    search,
  }: {
    limit?: number;
    pageSize?: number;
    page?: number;
    search?: string;
  } = {}
): Promise<DealsToMoveResponse> => {
  try {
    const response = await axios.get<Record<string, any>>(
      `${API_URL}${ROUTES.dealsToMove}`,
      {
        params: {
          source_deal: id,
          limit,
          page_size: pageSize,
          page,
          ...(search && { search }),
        },
      }
    );

    // Transform snake_case response to camelCase for frontend consistency
    // Handle potential missing fields gracefully
    const apiResponse = response.data;
    return {
      totalCount: apiResponse.total_count || apiResponse.totalCount || 0,
      pageSize: apiResponse.page_size || apiResponse.pageSize || pageSize,
      offset: apiResponse.offset || 0,
      page: apiResponse.page || page,
      results: apiResponse.results || [],
    };
  } catch (err) {
    notifyError("Failed to load applications to move applicant.");
    throw err;
  }
};

export const recalculateLumen = ({ id }: { id: Key }) =>
  axios.post(
    transformRoute(`${API_URL}${ROUTES.recalculateLumen}`, {
      id,
    })
  );

export const getApplicationIds = (dealId) =>
  axios
    .get(
      `${API_URL}${transformRoute(MANAGER_ROUTES.applicationIds, {
        id: dealId,
      })}`
    )
    .then(({ data }) => data);

export const getLedgerRecords = (params) =>
  axios.get(`${VEGA_BASE_URL}/api/ledger`, {
    params,
  });

interface DistinctLumenResultsResponse {
  companyId: number;
  distinctLumenResults: string[];
}

export const getDistinctLumenResults = (companyId: number) =>
  axios
    .get<DistinctLumenResultsResponse>(
      `${API_URL}/companies/${companyId}/distinct_lumen_results`
    )
    .then(({ data }) => data.distinctLumenResults);
